import {useCallback} from "react";
import {ProtectedItem} from "./ProtectedItem";
import {useAuthorization} from "./useAuthorization";

export type ProtectedItemPredicate<T extends ProtectedItem> = (
  item: T
) => boolean;

/**
 * The {@link useItemIsAllowed} hook produces a predicate function for testing
 * that a {@link ProtectedItem} has permissions defined and the current subject is
 * allowed access; when no permission is defined, it is treated as implicitly allowed.
 *
 * @see {@link useAuthorization}
 * @see {@link ProtectedItem}
 */
export const useItemIsAllowed = <
  T extends ProtectedItem
>(): ProtectedItemPredicate<T> => {
  const granted = useAuthorization();
  return useCallback<ProtectedItemPredicate<T>>(
    (item: T): boolean =>
      item.permissions && item.permissions.length > 0
        ? granted(item.permissions)
          ? (item.whenAuthorized || "show") === "show"
          : (item.whenUnauthorized || "hide") === "show"
        : true,
    [granted]
  );
};
